import { Box, Chip, Typography } from "@mui/material";
import FormSection from "../../../../../Components/Forms/FormSection/form-section";
import { SectionGlobalProps } from "../settings-global";
import { createRef, useEffect, useMemo, useState } from "react";
import { userService } from "../../../../../Core/Api/services";
import React from "react";
import { useTranslation } from "react-i18next";
import UploadCard, { UploadCardRef, UploadContext } from "../../../../../Components/Cards/UploadCard/upload-card";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import { useSelector } from "react-redux";
import { GetUploadCardState } from "../../../../../Redux/Reducers/redux-slice-aspera-upload";
import { Stack, useTheme } from "@mui/material";
import { PlatformResourceType } from "../../../../../Core/Api/Types/platform-resource-type";

const UploadRessource = (props: { index: number; type: PlatformResourceType; uploadStatus: string }) => {
  const { index, type, uploadStatus } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const [uploadCardsRef, setUploadCardsRef] = useState<React.RefObject<UploadCardRef | null>>(createRef());
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (uploadStatus === "readyToUpload") uploadCardsRef.current?.performUpload();
    if (uploadStatus === "noFile") setReload((x) => x + 1);
  }, [uploadStatus]);

  const cardTitle = useMemo(() => {
    switch (type) {
      case PlatformResourceType.LogoV4light:
        return t("button:uploadLightLogo");
      case PlatformResourceType.LogoV4dark:
        return t("button:uploadDarkLogo");
      case PlatformResourceType.LogoEmail:
        return t("button:uploadLogoEmail");
      default:
        return t("common:upload") + " " + type;
    }
  }, [type]);

  return (
    <Stack
      border={`1px solid ${theme.palette.background.alphaHover}`}
      borderRadius={index % 2 === 0 ? "12px 0px 0px 12px" : "0px 12px 12px 0px"}
      sx={{ backgroundColor: theme.palette.background[200] }}
      direction={index % 2 === 0 ? "row" : "row-reverse"}
      spacing={1}
      p={1}
      mb={1}
      width={"50%"}
      minWidth={"650px"}
      display={"flex"}
    >
      <UploadCard
        ref={uploadCardsRef}
        objectId={-1}
        cardIndex={index}
        title={cardTitle}
        userHasRightToDelete={false}
        context={UploadContext.PlatformResource}
        resourceType={type}
        format={{
          id: index,
          isUploaded: false,
          maxFileSizeMb: 80,
          name: "File",
          userHasRighToDelete: false,
          acceptsFiles: { ["application/octet-stream"]: [".jpg", ".jpeg", ".png"] },
          description: "Certificate",
          isUserDataMandatory: false,
          objectType: ObjectType.Image,
        }}
      />
      <Stack
        borderRadius={"6px"}
        border={`1px dashed ${theme.palette.backgroundAlpha.border}`}
        direction={"column"}
        spacing={1}
        p={1}
        width={"250px"}
        position={"relative"}
        display={"flex"}
        sx={{ backgroundColor: theme.palette.background.paper }}
      >
        <Stack direction={"row"} justifyContent={"space-between"} display={"flex"}>
          <Typography variant="body2" color="text.tier">
            {t("common:current")}
          </Typography>
          <Chip label={type} size="micro" />
        </Stack>
        <Stack height="100%" justifyContent="center" ml={1}>
          <img
            src={userService.GetResourceUrl(type) + "?" + reload}
            style={{ objectFit: "contain", zIndex: 1299, height: "84px" }}
          />
        </Stack>
        <Box
          position={"absolute"}
          top={0}
          left={0}
          right={0}
          bottom={0}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          zIndex={1}
        >
          <Typography variant="body2" color="text.tier">
            {t("form:statusText.noLogo")}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
};

const SettingsGlobalSectionRessourceImages = (props: SectionGlobalProps) => {
  const { t } = useTranslation();
  const uploadStatusList = useSelector(GetUploadCardState);

  return (
    <FormSection title={t("form:section.title.uploadResourceImages")} status="todo" sx={{ mb: 2 }}>
      <Stack direction={"row"} flexWrap={"wrap"} justifyContent={"space-between"} display={"flex"}>
        {Object.keys(PlatformResourceType)
          .filter((type) =>
            [
              PlatformResourceType.LogoV4dark,
              PlatformResourceType.LogoV4light,
              PlatformResourceType.LogoEmail,
            ].includes(type as PlatformResourceType)
          )
          .map((type, i) => (
            <UploadRessource
              key={type}
              uploadStatus={uploadStatusList.find((x) => x.uploadCardIndex === i)?.uploadCardStatus ?? ""}
              type={type as PlatformResourceType}
              index={i}
            />
          ))}
      </Stack>
    </FormSection>
  );
};

export default SettingsGlobalSectionRessourceImages;
